import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import withTheme from '@material-ui/styles/withTheme';
import {
    Grid,
    CircularProgress,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody
} from '@material-ui/core';
import Button from '@material-ui/core/Button';
import { useHistory } from 'react-router';
import { Form, Formik } from 'formik';
import { useNotify } from '../../../../hooks/useNotify';
import { useSwaggerClient, doSwaggerCall } from '../../../../hooks/useSwaggerClient';
import FormSection from '../../../../components/atom/FormSection';
import AdminHeader from '../../../../components/atom/AdminHeader';
import { ErrorIcon } from '../../../../icons';
import { RouterPrompt } from '../../../../components/atom/RouterPrompt';
import FormikInput from '../../../../components/atom/FormikInput';
import AutocompleteSelect from '../../../../components/atom/AutocompleteSelect';
import { activityAutocomplete } from '../../../../autocomplete';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';

function MultipleSelectCheckboxList({ options, setValue, value, discountCompanyId }) {
    return (
        <Table>
            <TableHead>
                <TableRow>
                    <TableCell style={{ width: 1 }}></TableCell>
                    <TableCell>User group</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {options.map(option => (
                    <TableRow key={option.id}>
                        <TableCell>
                            <Checkbox
                                checked={value.includes(option.id)}
                                onChange={event => {
                                    if (event.target.checked) {
                                        setValue([...value, option.id]);
                                    } else {
                                        setValue(value.filter(id => id !== option.id));
                                    }
                                }}
                            />
                        </TableCell>
                        <TableCell>
                            <span
                                style={{
                                    color: option.company_id === discountCompanyId ? '' : 'grey'
                                }}
                            >
                                {option.name}
                                <small style={{ float: 'right' }}>
                                    {option.company_id !== discountCompanyId &&
                                        ` (group in a different company: ${option.company_id})`}
                                </small>
                            </span>
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
}
function DiscountPackageEditPage({ clientId, theme }) {
    const { discountPackageId } = useParams();
    const { notifyError, notifySuccess } = useNotify();
    const history = useHistory();
    const [submitdiscountPackage, setSubmitDiscountPackage] = useState(false);
    const [availableUserGroups, setAvailableUserGroups] = useState([]);
    const DISCOUNT_TYPES = [
        {
            type: '',
            name: 'None'
        },
        {
            type: '$',
            name: 'USD ($)'
        },
        {
            type: '%',
            name: 'Percentage (%)'
        }
    ];
    const [discountPackage, loading, error] = useSwaggerClient(
        'discountpackages',
        'getDiscountPackageById',
        {
            discountPackageId: parseInt(discountPackageId, 10)
        }
    );

    useEffect(() => {
        return doSwaggerCall('usergroups', 'getAllUserGroups', {
            pageSize: 10000,
            search: ''
        }).then(({ userGroups }) =>
            setAvailableUserGroups(
                userGroups
                    .map(e => {
                        return { id: e.id, name: `${e.name}`, company_id: e.company_id };
                    })
                    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
            )
        );
    }, [setAvailableUserGroups]);

    const onSubmit = (values, { setSubmitting }) => {
        setSubmitting(true);
        doSwaggerCall('discountpackages', 'updateDiscountPackage', {
            discountPackage: {
                name: values.name,
                wholecart: values.wholecart,
                userGroups: values.userGroups,
                timeshareactivity_id: values.timeshareactivity_id,
                discount: {
                    agencyDiscount: {
                        amount: values.discount.agencyDiscount.amount,
                        type: values.discount.agencyDiscount.type
                    },
                    vendorDiscount: {
                        amount: values.discount.vendorDiscount.amount,
                        type: values.discount.vendorDiscount.type
                    }
                }
            },
            discountPackageId: parseInt(discountPackageId, 10)
        })
            .then(() => {
                notifySuccess('discountPackage modified!');
                setSubmitting(false);
                setSubmitDiscountPackage(true);
                history.push(`/admin/client/${clientId}/discountpackages`);
            })
            .catch(() => {
                notifyError('Could not modify discountPackage!');
                setSubmitting(false);
            });
    };

    if (error) {
        return (
            <Grid container justifyContent="center">
                <h1>{error}</h1>
            </Grid>
        );
    }
    if (loading) {
        return (
            <Grid container justifyContent="center">
                <CircularProgress />
            </Grid>
        );
    }
    return (
        <Formik onSubmit={onSubmit} initialValues={discountPackage}>
            {({ isSubmitting, values, setFieldValue, dirty }) => (
                <>
                    <RouterPrompt when={!!dirty && !submitdiscountPackage} />
                    <Form>
                        <Grid container spacing={2}>
                            <AdminHeader title={`Discount package: ${discountPackage?.name}`} />
                            {!!error && (
                                <Grid
                                    item
                                    container
                                    spacing={2}
                                    xs={12}
                                    style={{
                                        color: theme.palette.error.main
                                    }}
                                    alignItems="center"
                                >
                                    <Grid item>
                                        <ErrorIcon color="error" />
                                    </Grid>
                                    <Grid item>
                                        <span>{error.response.body.message}</span>
                                    </Grid>
                                </Grid>
                            )}
                            <FormSection title="Generic information">
                                <FormikInput name="name" label="Name*" xs={6} sm={6} />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={!values.wholecart}
                                            onChange={e =>
                                                setFieldValue('wholecart', !e.target.checked)
                                            }
                                        />
                                    }
                                    label="Per item discount"
                                />
                                <Grid item xs={12}>
                                    <AutocompleteSelect
                                        initCall={activityAutocomplete.init}
                                        autocompleteCall={activityAutocomplete.search}
                                        value={values.timeshareactivity_id}
                                        setValue={newValue => {
                                            setFieldValue('timeshareactivity_id', newValue);
                                        }}
                                    >
                                        <TextField label="Timeshare Activity" />
                                    </AutocompleteSelect>
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        type="text"
                                        label={'Agency discount amount'}
                                        value={values.discount?.agencyDiscount?.amount}
                                        onChange={event => {
                                            setFieldValue(
                                                'discount.agencyDiscount.amount',
                                                event.target.value
                                            );
                                        }}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <FormControl fullWidth>
                                        <InputLabel id="agency_discount_type">
                                            Agency discount type
                                        </InputLabel>
                                        <Select
                                            label={'Agency discount type'}
                                            value={values.discount?.agencyDiscount?.type || ''}
                                            onChange={event => {
                                                setFieldValue(
                                                    'discount.agencyDiscount.type',
                                                    event.target.value
                                                );
                                            }}
                                            fullWidth
                                        >
                                            {DISCOUNT_TYPES.map(fieldType => (
                                                <MenuItem
                                                    key={fieldType.name}
                                                    value={fieldType.type}
                                                >
                                                    {fieldType.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        type="text"
                                        label={'Vendor discount amount'}
                                        value={values.discount?.vendorDiscount?.amount}
                                        onChange={event => {
                                            setFieldValue(
                                                'discount.vendorDiscount.amount',
                                                event.target.value
                                            );
                                        }}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <FormControl fullWidth>
                                        <InputLabel id="vendor_discount_type">
                                            Vendor discount type
                                        </InputLabel>
                                        <Select
                                            label={'Vendor discount type'}
                                            value={values.discount?.vendorDiscount?.type || ''}
                                            onChange={event => {
                                                setFieldValue(
                                                    'discount.vendorDiscount.type',
                                                    event.target.value
                                                );
                                            }}
                                            fullWidth
                                        >
                                            {DISCOUNT_TYPES.map(fieldType => (
                                                <MenuItem
                                                    key={fieldType.name}
                                                    value={fieldType.type}
                                                >
                                                    {fieldType.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <MultipleSelectCheckboxList
                                        discountCompanyId={discountPackage?.client_id}
                                        options={availableUserGroups}
                                        setValue={value => {
                                            if (value === null) {
                                                setFieldValue('userGroups', []);
                                                return;
                                            }
                                            setFieldValue('userGroups', value);
                                        }}
                                        value={values?.userGroups}
                                    />
                                </Grid>
                            </FormSection>
                            <Grid item xs={6}>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    color="primary"
                                    type="submit"
                                    disabled={isSubmitting || !values.name}
                                >
                                    Save
                                </Button>
                            </Grid>
                            <Grid item xs={6}>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={() =>
                                        history.push(`/admin/client/${clientId}/discountpackages`)
                                    }
                                >
                                    Back
                                </Button>
                            </Grid>
                        </Grid>
                    </Form>
                </>
            )}
        </Formik>
    );
}

export default withTheme(DiscountPackageEditPage);
